import { useContext } from "react";

import { useTranslation } from "react-i18next";

import DateTime from "components/DateTime";
import ScheduleTask from "components/ScheduleTask";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    Column,
    COLUMN_IDENTIFIERS,
    COLUMN_NAMES,
    COLUMN_RESOURCES,
    COLUMNS_SCHEMA_VERSION,
    ENDPOINTS,
    FILTER_NAMES,
    InitialColumn,
    NOT_FILTER_NAMES,
    parseResource,
    Resource,
    TABLE_NAMES,
    useFileSize,
    useFormattedNumber
} from "shared";

import DesktopLicenseStatusIcon from "../TableItem/CustomValues/DesktopLicenseStatusIcon";
import DeviceModelTaskTypeSupport from "../TableItem/CustomValues/DeviceModelTaskTypeSupport";
import DeviceStatusIcon from "../TableItem/CustomValues/DeviceStatusIcon";
import PermissionMatrix from "../TableItem/CustomValues/PermissionMatrix";
import QueueIcon from "../TableItem/CustomValues/QueueIcon";
import Role from "../TableItem/CustomValues/Role";
import RolePermissions from "../TableItem/CustomValues/RolePermissions";
import SyncMode from "../TableItem/CustomValues/SyncMode";
import SyncStatus from "../TableItem/CustomValues/SyncStatus";
import TaskStatusIcon from "../TableItem/CustomValues/TaskStatusIcon";
import TaskTypeCompatibilities from "../TableItem/CustomValues/TaskTypeCompatibilities";
import UserAccessGroups from "../TableItem/CustomValues/UserAccessGroups";
import UserSentConfiguration from "../TableItem/CustomValues/UserSentConfiguration";
import UserStatus from "../TableItem/CustomValues/UserStatus";

export const useTableFunctions = () => {
    const { user } = useContext(AuthContext);

    const { setQueryParams } = useContext(ParamsContext);

    const { isInnerTable, setInnerTableParams } = useContext(TableContext);

    const { t } = useTranslation();
    const { renderFileSize } = useFileSize();
    const { renderFormattedNumber } = useFormattedNumber();

    const addIsActiveToColumns = (columns: InitialColumn[]) =>
        columns.map(column => ({
            ...column,
            isActive: Boolean(column.isDefaultActive)
        }));

    const getIsActiveColumnFromLocalStorage = (
        name: TABLE_NAMES,
        columnName: string,
        defaultValue: boolean
    ) => {
        try {
            const parsed = JSON.parse(
                localStorage.getItem(`${name}Columns`) ?? "{}"
            ) as Record<string, boolean>;

            return parsed[columnName] ?? defaultValue;
        } catch {
            return defaultValue;
        }
    };

    const checkIfAllowedToRender = (columns: InitialColumn[]) =>
        columns.filter(({ isAllowedToRender, accessLevelRequiredToRender }) => {
            const isAllowedInGeneral =
                isAllowedToRender === undefined || isAllowedToRender;

            const isValidUser =
                accessLevelRequiredToRender === undefined ||
                Boolean(
                    user && user.access_level >= accessLevelRequiredToRender
                );

            return isAllowedInGeneral && isValidUser;
        });

    const getDefaultColumns = (columns: InitialColumn[]) =>
        Object.fromEntries(
            columns.map(col => [col.name, Boolean(col.isDefaultActive)])
        );

    const resetColumnsOnSchemaMismatch = (
        tableName: TABLE_NAMES,
        columns: InitialColumn[]
    ) => {
        const versionKey = `${tableName}ColumnsVersion`;
        const storedVersion = Number(localStorage.getItem(versionKey));

        if (storedVersion === COLUMNS_SCHEMA_VERSION) return;

        const defaults = getDefaultColumns(columns);

        localStorage.setItem(`${tableName}Columns`, JSON.stringify(defaults));
        localStorage.setItem(versionKey, String(COLUMNS_SCHEMA_VERSION));
    };

    const normalizeColumnsInLocalStorage = (
        name: TABLE_NAMES,
        columns: InitialColumn[]
    ) => {
        const storageKey = `${name}Columns`;
        const columnsString = localStorage.getItem(storageKey);

        const defaults = getDefaultColumns(columns);

        if (!columnsString) {
            localStorage.setItem(storageKey, JSON.stringify(defaults));
            return;
        }

        try {
            const parsed = JSON.parse(columnsString) as Record<string, unknown>;
            const normalized: Record<string, boolean> = {};

            columns.forEach(col => {
                const storedValue = parsed[col.name];
                const isCorrectValue = typeof storedValue === "boolean";

                normalized[col.name] =
                    col.name in parsed && isCorrectValue
                        ? storedValue
                        : Boolean(col.isDefaultActive);
            });

            localStorage.setItem(storageKey, JSON.stringify(normalized));
        } catch {
            localStorage.setItem(storageKey, JSON.stringify(defaults));
        }
    };

    const renderColumnsWithIsActive = (
        name: TABLE_NAMES,
        initialColumns: InitialColumn[]
    ) => {
        const columnsAllowedToRender = checkIfAllowedToRender(initialColumns);

        resetColumnsOnSchemaMismatch(name, columnsAllowedToRender);

        normalizeColumnsInLocalStorage(name, columnsAllowedToRender);

        const columnsWithIsActive = addIsActiveToColumns(
            columnsAllowedToRender
        );

        return columnsWithIsActive.map((column: Column) => ({
            ...column,
            isActive: getIsActiveColumnFromLocalStorage(
                name,
                column.name,
                column.isActive
            )
        }));
    };

    const renderStringResource = (
        name: COLUMN_NAMES,
        value: string | number | null,
        customDateTimeFormat: string,
        isTranslatable: boolean
    ) => {
        const dateTimeValues = [
            COLUMN_NAMES.ChangedAt,
            COLUMN_NAMES.CreatedAt,
            COLUMN_NAMES.DownloadAt,
            COLUMN_NAMES.Expiration,
            COLUMN_NAMES.ExpiresAt,
            COLUMN_NAMES.LastCheckedAt,
            COLUMN_NAMES.LastLoginAt,
            COLUMN_NAMES.LastStatusUpdate,
            COLUMN_NAMES.LastUsedAt,
            COLUMN_NAMES.SeenAt,
            COLUMN_NAMES.SyncedAt,
            COLUMN_NAMES.UpdatedAt
        ];

        if (value === null || value === "") {
            return "-";
        }

        if (dateTimeValues.includes(name)) {
            const dateTimeFormat = customDateTimeFormat || "L LTS";

            return <DateTime date={String(value)} format={dateTimeFormat} />;
        }

        if (isTranslatable) {
            return name === COLUMN_NAMES.Field
                ? t(`General##${String(value).toLowerCase()}`)
                : t(`General##${value}`);
        }

        if (name === COLUMN_NAMES.Size) {
            return renderFileSize(value);
        }

        if (name === COLUMN_NAMES.DevicesCount) {
            return renderFormattedNumber(Number(value));
        }

        return value;
    };

    const renderValue = (
        name: COLUMN_NAMES,
        resource: Resource,
        data: any,
        columnIdentifier: COLUMN_IDENTIFIERS | undefined,
        customDateTimeFormat: string = "L LTS",
        isTranslatable: boolean = false
    ) => {
        switch (columnIdentifier) {
            case COLUMN_IDENTIFIERS.DeviceActivityStatus: {
                return (
                    <DeviceStatusIcon
                        status={data[COLUMN_RESOURCES.DeviceStatusIcon]}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.DeviceTaskQueue: {
                return <QueueIcon status={data[COLUMN_RESOURCES.QueueIcon]} />;
            }
            case COLUMN_IDENTIFIERS.TaskStatusId: {
                const { verify_attempt_count } = data;

                return (
                    <TaskStatusIcon
                        id={data[COLUMN_RESOURCES.TaskStatusIcon]}
                        verifyAttemptCount={verify_attempt_count}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.DesktopLicenseStatus: {
                return (
                    <DesktopLicenseStatusIcon
                        isEnabled={
                            data[COLUMN_RESOURCES.DesktopLicenseStatusIcon]
                        }
                    />
                );
            }
            case COLUMN_IDENTIFIERS.WebEditorSupport: {
                return (
                    <DeviceModelTaskTypeSupport
                        isSupported={data[COLUMN_RESOURCES.WebEditorSupport]}
                        titleKey="Table##model task type support"
                    />
                );
            }
            case COLUMN_IDENTIFIERS.TaskTypeCompatibilities: {
                return (
                    <TaskTypeCompatibilities
                        taskTypes={
                            data[COLUMN_RESOURCES.TaskTypeCompatibilities]
                        }
                    />
                );
            }
            case COLUMN_IDENTIFIERS.TaskSupport: {
                return (
                    <DeviceModelTaskTypeSupport
                        isSupported={data[COLUMN_RESOURCES.TaskSupport]}
                        titleKey={`General##boolean filtered value name##${FILTER_NAMES.TaskSupport}`}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.Schedule: {
                return (
                    <ScheduleTask
                        schedule={data.schedule}
                        format="L"
                        shouldBeAdjusted
                    />
                );
            }
            case COLUMN_IDENTIFIERS.Roles: {
                return <Role roles={data[COLUMN_RESOURCES.Roles]} />;
            }
            case COLUMN_IDENTIFIERS.RolePermissions: {
                return (
                    <RolePermissions
                        permissions={data[COLUMN_RESOURCES.Permissions]}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.RoleStatuses: {
                const roleStatuses = data[COLUMN_RESOURCES.RoleStatuses];
                const roleId = resource[1] as number;
                const isEnabled = roleStatuses[roleId];

                return isEnabled ? <PermissionMatrix /> : "-";
            }
            case COLUMN_IDENTIFIERS.UserStatus: {
                return (
                    <UserStatus
                        status={data[COLUMN_RESOURCES.Status]}
                        activeExpiresAt={data[COLUMN_RESOURCES.ExpiresAt]}
                        invitation={data[COLUMN_RESOURCES.Invitation]}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.UserAccessGroups: {
                return (
                    <UserAccessGroups
                        accessGroups={data[COLUMN_RESOURCES.AccessGroups]}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.UserSentConfiguration: {
                return (
                    <UserSentConfiguration
                        fileName={data[COLUMN_RESOURCES.UserSentConfiguration]}
                    />
                );
            }
            case COLUMN_IDENTIFIERS.SyncMode: {
                return <SyncMode data={data} />;
            }
            case COLUMN_IDENTIFIERS.SyncStatus: {
                return <SyncStatus data={data} />;
            }
            default: {
                const parsedValue = parseResource(resource, data);

                return renderStringResource(
                    name,
                    parsedValue,
                    customDateTimeFormat,
                    isTranslatable
                );
            }
        }
    };

    const handlePageChange = (newPage: number) => {
        const pageValue = newPage === 1 ? "" : String(newPage);
        const pageQueryParam = { [NOT_FILTER_NAMES.Page]: pageValue };

        isInnerTable
            ? setInnerTableParams({ params: pageQueryParam })
            : setQueryParams({
                  params: pageQueryParam,
                  resetPage: false,
                  removeSelected: true
              });
    };

    const getEditRowEndpoint = (tableName: TABLE_NAMES, id: number) => {
        const isDesktopLicensesTable =
            tableName === TABLE_NAMES.DesktopLicenses;

        return `${isDesktopLicensesTable ? ENDPOINTS.DesktopLicenses : tableName}/${id}`;
    };

    return {
        renderColumnsWithIsActive,
        renderValue,
        handlePageChange,
        getEditRowEndpoint
    };
};
